'use client'

import Link from 'next/link'

import { Callout } from '@/app/components/Callout'
import { useDownloadModal } from '@/app/providers/DownloadModalProvider'
import { useSettings } from '@/app/providers/SettingsProvider'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { detectDevice } from '@/app/utils/device'
import DownloadAppStore from '@/public/images/downloadAppStore.svg'
import DownloadGooglePlay from '@/public/images/downloadGooglePlay.svg'
import type { ThreeSteps as ThreeStepsProps } from '@/sanity.types'

type ThreeStepsComponentProps = {
  block: ThreeStepsProps & { _key?: string }
  index: number
  pageId?: string
  pageType?: string
}

export default function ThreeSteps({ block }: ThreeStepsComponentProps) {
  const settings = useSettings()
  const { openModal } = useDownloadModal()
  // Default to true if undefined (matches initialValue in schema)
  const showButtons = block.showDownloadButtons ?? true
  const deviceType = detectDevice()
  const isDesktop = deviceType === 'desktop'

  const handleDownloadClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isDesktop) {
      e.preventDefault()
      openModal()
    }
  }

  if (!block?.items?.length) return null

  return (
    <div className="content-container my-7xl lg:my-11xl">
      <div className="bg-neutral-50 rounded-md p-2xl md:p-5xl lg:py-9xl lg:rounded-lg">
        <div className="mx-auto max-w-[1010px] flex flex-col items-center justify-center gap-2xl lg:gap-5xl">
          {(block.header || block.subheader) && (
            <div className="flex flex-col gap-md text-center">
              {block.header && (
                <p className="font-bold size-subheading-lg lg:size-subheading-2xl">
                  {block.header}
                </p>
              )}
              {block.subheader && (
                <p className="font-bold size-subheading-sm lg:size-subheading-lg">
                  {block.subheader}
                </p>
              )}
            </div>
          )}

          <div className="flex w-full flex-col items-center gap-2xl md:flex-row md:items-stretch lg:gap-5xl">
            {block.items.map((item, idx) => {
              if (!item) return null

              return (
                <Callout
                  key={item._key || idx}
                  callout={item}
                  className="flex w-full max-w-[310px] md:flex-1"
                  tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
                />
              )
            })}
          </div>
        </div>
        {showButtons && (settings?.appStoreUrl || settings?.googlePlayUrl) && (
          <div className="mx-auto mt-2xl flex w-full gap-sm max-w-[254px] md:gap-2xl lg:max-w-[380px] lg:gap-5xl lg:mt-5xl">
            {settings?.appStoreUrl && (
              <Link
                className="flex-1"
                href={settings.appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download App Store"
                onClick={handleDownloadClick}
              >
                <DownloadAppStore className="h-auto w-full" />
              </Link>
            )}
            {settings?.googlePlayUrl && (
              <Link
                className="flex-1"
                href={settings.googlePlayUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download Google Play"
                onClick={handleDownloadClick}
              >
                <DownloadGooglePlay className="h-auto w-full" />
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
